import { CalendarClock } from "lucide-react";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { PageHelmet } from "@/src/common/components/page-helmet";
import { toLanguage } from "@/src/common/locales/i18n";
import { LegalContactCard } from "@/src/legal/components/legal-contact-card";
import { LegalHero } from "@/src/legal/components/legal-hero";
import { LegalToc } from "@/src/legal/components/legal-toc";
import { LEGAL_MARKDOWN, type LegalDocumentKey } from "@/src/legal/constants/legal-content";
import { parseLegalDocument, splitLegalSections } from "@/src/legal/utils/legal-markdown.utils";

interface LegalPageLayoutProps {
	documentKey: LegalDocumentKey;
	layout?: "article" | "cards";
}

const PROSE_CLASS =
	"legal-article prose prose-neutral max-w-none text-pretty prose-headings:scroll-mt-28 prose-headings:text-foreground prose-h2:text-2xl prose-h2:font-semibold prose-h2:tracking-tight prose-h2:mt-14 prose-h2:mb-5 prose-h3:text-lg prose-h3:mt-8 prose-p:text-default-700 prose-p:leading-relaxed prose-a:font-medium prose-a:text-brand-600 prose-a:no-underline hover:prose-a:underline prose-strong:text-foreground prose-li:text-default-700 prose-li:marker:text-brand-400 prose-hr:border-default-200";

function LegalArticle({ documentKey, markdown }: { documentKey: LegalDocumentKey; markdown: string }) {
	const { t } = useTranslation("legal");
	const { html, headings } = useMemo(() => parseLegalDocument(markdown), [markdown]);
	const effectiveDate = t(`${documentKey}.effective_date`, { defaultValue: "" });

	return (
		<div className="mx-auto max-w-6xl px-4 pb-24 sm:px-6 lg:grid lg:grid-cols-[240px_minmax(0,1fr)] lg:gap-16 lg:px-8">
			<LegalToc headings={headings} />

			<article className="min-w-0">
				{effectiveDate.length > 0 && (
					<span className="inline-flex items-center gap-2 rounded-full border border-default-200 bg-content1 px-3 py-1 text-xs font-medium text-default-600">
						<CalendarClock className="h-3.5 w-3.5 text-brand-500" />
						{t("last_updated")} · {effectiveDate}
					</span>
				)}
				<div
					className={PROSE_CLASS}
					// biome-ignore lint/security/noDangerouslySetInnerHtml: trusted markdown rendered via marked
					dangerouslySetInnerHTML={{ __html: html }}
				/>
				<LegalContactCard />
			</article>
		</div>
	);
}

function LegalCards({ markdown }: { markdown: string }) {
	const sections = useMemo(() => splitLegalSections(markdown), [markdown]);

	return (
		<div className="mx-auto max-w-5xl px-4 pb-24 sm:px-6 lg:px-8">
			<div className="grid gap-5 md:grid-cols-2">
				{sections.map((section) => (
					<section key={section.id} id={section.id} className="shadow-card rounded-2xl border border-default-200 bg-content1 p-6 md:p-7">
						<h2 className="text-lg font-semibold tracking-tight text-foreground">{section.title}</h2>
						<div
							className="prose prose-neutral mt-3 max-w-none text-pretty prose-p:my-2 prose-p:text-sm prose-p:leading-relaxed prose-p:text-default-700 prose-a:font-medium prose-a:text-brand-600 prose-a:no-underline hover:prose-a:underline prose-strong:text-foreground"
							// biome-ignore lint/security/noDangerouslySetInnerHtml: trusted markdown rendered via marked
							dangerouslySetInnerHTML={{ __html: section.html }}
						/>
					</section>
				))}
			</div>
			<LegalContactCard />
		</div>
	);
}

export function LegalPageLayout({ documentKey, layout = "article" }: LegalPageLayoutProps) {
	const { t, i18n } = useTranslation("legal");
	const language = toLanguage(i18n.resolvedLanguage);
	const markdown = LEGAL_MARKDOWN[language][documentKey];

	return (
		<>
			<PageHelmet title={t(`${documentKey}.meta.title`)} description={t(`${documentKey}.meta.description`)} />
			<LegalHero documentKey={documentKey} />
			{layout === "cards" ? <LegalCards markdown={markdown} /> : <LegalArticle documentKey={documentKey} markdown={markdown} />}
		</>
	);
}
